import React from 'react';
import { useStore } from 'effector-react';
import type { HSIconImage, HSIconSeqFrame, HSIconVideo } from '@types';
import Slider from '@components/Slider';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import FeatureToggle from '@components/FeatureToggle';
import { roundNum } from '@utils/roundNum';

const MIN = 0.01;
const MAX = 2;
const STEP = 0.001;

type Key = 'scaleX' | 'scaleY' | 'scale';
type Icon = HSIconImage | HSIconSeqFrame | HSIconVideo;

const valToString = (val: number) => `${roundNum(1, val * 100)}%`;

function TabScale() {
  const { hotspot, isBatch, batchHotspots } = useStore($editingHotspot)!;
  const icon = hotspot.icon as Icon;

  const handleScaleChange = (key: Key) => (val: number) => {
    if (key === 'scale') {
      $editingHotspotApi.update((draft) => {
        (draft.icon as Icon).scaleX = val;
        (draft.icon as Icon).scaleY = val;
      });
      if (!isBatch) {
        MainPlayer.get()?.updateHotspot({
          id: hotspot.id,
          scaleX: val,
          scaleY: val,
        });
      } else {
        (batchHotspots ?? []).forEach((item) => {
          MainPlayer.get()?.updateHotspot({
            id: item.id,
            scaleX: val,
            scaleY: val,
          });
        });
      }
    } else {
      $editingHotspotApi.update((draft) => {
        (draft.icon as Icon)[key] = val;
      });
      if (!isBatch) {
        MainPlayer.get()?.updateHotspot({ id: hotspot.id, [key]: val });
      } else {
        (batchHotspots ?? []).forEach((item) => {
          MainPlayer.get()?.updateHotspot({ id: item.id, [key]: val });
        });
      }
    }
  };

  const handleLockToggle = (bool: boolean) => {
    if (bool) {
      const scale = icon.scaleX;
      $editingHotspotApi.update((draft) => {
        (draft.icon as Icon).scaleLock = 1;
        (draft.icon as Icon).scaleX = scale;
        (draft.icon as Icon).scaleY = scale;
      });
      if (!isBatch) {
        MainPlayer.get()?.updateHotspot({
          id: hotspot.id,
          scaleX: scale,
          scaleY: scale,
        });
      } else {
        (batchHotspots ?? []).forEach((item) => {
          MainPlayer.get()?.updateHotspot({
            id: item.id,
            scaleX: scale,
            scaleY: scale,
          });
        });
      }
    } else {
      $editingHotspotApi.update((draft) => {
        (draft.icon as Icon).scaleLock = 0;
      });
    }
  };

  return (
    <>
      {icon.scaleLock ? (
        <Slider
          min={MIN}
          max={MAX}
          value={icon.scaleX}
          valToString={valToString}
          onChange={handleScaleChange('scale')}
        >
          <Slider.Header title="缩放：" />
          <Slider.Bar step={STEP} />
          <Slider.Control step={STEP} />
        </Slider>
      ) : (
        <>
          <Slider
            min={MIN}
            max={MAX}
            value={icon.scaleX}
            valToString={valToString}
            onChange={handleScaleChange('scaleX')}
          >
            <Slider.Header title="缩放宽度：" />
            <Slider.Bar step={STEP} />
            <Slider.Control step={STEP} />
          </Slider>
          <Slider
            className="mt-20"
            min={MIN}
            max={MAX}
            value={icon.scaleY}
            valToString={valToString}
            onChange={handleScaleChange('scaleY')}
          >
            <Slider.Header title="缩放高度：" />
            <Slider.Bar step={STEP} />
            <Slider.Control step={STEP} />
          </Slider>
        </>
      )}
      <FeatureToggle
        className="mt-20"
        title="锁定宽高比："
        isOn={!!icon.scaleLock}
        onToggle={handleLockToggle}
      />
    </>
  );
}

export default TabScale;
